/**
 * 월패드(HT) 지도 생성 — 현장 상세의 '월패드 지도' 섹션.
 *
 * 맵을 스크린샷으로 "찍는" 대신 고정 캔버스(2168x1528 = HT 규격 542x382 의 4배)에
 * "계산해서 그리기" 때문에, 그릴 때 쓴 변환이 곧 좌표 계수다. 예전처럼 캡처 이미지에
 * CCTV 점을 피팅(map_fit.py)할 필요가 없고 잔차도 없다.
 *
 * 화면이 지켜야 할 것 하나: **이미지와 계수는 한 세트**다. 지도를 다시 뽑으면
 * HT 에 등록하는 이미지와 ht_location 의 env 계수를 반드시 함께 교체해야 한다.
 * 한쪽만 바꾸면 월패드에서 주차 위치 마커가 밀린다.
 */
import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Map as MapIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material';

import WallpadMapService, { WallpadMapFloor, WallpadMapResult } from '../../../services/WallpadMapService';

export interface WallpadMapSectionProps {
  /** 표시용 현장 이름 — 생성 결과 파일명 접두사로도 쓴다. */
  siteName?: string;
}

/**
 * 기본 맵 주소. 본사 서버에서는 manage 백엔드와 맵이 같은 docker 네트워크에 있어
 * 컨테이너 이름으로 바로 닿는다. 원격 현장은 그 현장의 맵 주소로 바꿔 넣는다.
 */
const DEFAULT_MAP_URL = 'http://smartparking-map.app';

/** 브라우저 다운로드 트리거 — 결과는 data URL 이라 서버 왕복이 없다. */
function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * 층 하나의 검증 배지.
 * validated=false 면 수치를 못 읽은 것이므로 "정상" 으로 표시하지 않는다.
 */
const VerdictChip: React.FC<{ floor: WallpadMapFloor }> = ({ floor }) => {
  if (!floor.validated) {
    return <Chip size="small" color="default" variant="outlined" label="검증값 확인 불가" />;
  }
  const bad = floor.max_error_px > 1 || floor.out_of_range > 0;
  return (
    <Chip
      size="small"
      color={bad ? 'error' : 'success'}
      variant={bad ? 'filled' : 'outlined'}
      label={
        `CCTV ${floor.cctv_count}개 · 최대오차 ${floor.max_error_px.toFixed(3)}px` +
        (floor.out_of_range > 0 ? ` · 범위밖 ${floor.out_of_range}` : '')
      }
    />
  );
};

const WallpadMapSection: React.FC<WallpadMapSectionProps> = ({ siteName }) => {
  const [mapUrl, setMapUrl] = useState(DEFAULT_MAP_URL);
  const [lotId, setLotId] = useState('1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<WallpadMapResult | null>(null);
  const [copied, setCopied] = useState(false);

  const lotIdNum = Number(lotId);
  const canRun = !loading && mapUrl.trim() !== '' && Number.isInteger(lotIdNum) && lotIdNum > 0;

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setCopied(false);
    try {
      setResult(await WallpadMapService.generate(mapUrl.trim(), lotIdNum));
    } catch (e) {
      setError(e instanceof Error ? e.message : '월패드 지도 생성 실패');
    } finally {
      setLoading(false);
    }
  };

  const copyTransform = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.transform_env);
      setCopied(true);
    } catch {
      setError('클립보드 복사에 실패했습니다. 아래 값을 직접 선택해 복사하세요.');
    }
  };

  const filePrefix = siteName ? `${siteName}_` : '';

  return (
    <Box data-testid="wallpad-map-section">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <MapIcon color="action" />
        <Typography variant="h6" fontWeight={700}>
          월패드 지도
        </Typography>
        <Typography variant="caption" color="text.secondary">
          HT 등록용 542×382 이미지와 좌표 계수를 함께 생성합니다
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label="맵 주소"
            value={mapUrl}
            onChange={(e) => setMapUrl(e.target.value)}
            size="small"
            fullWidth
            disabled={loading}
            helperText="parking-map-client 주소. 본사는 기본값 그대로, 원격 현장은 그 현장 주소."
          />
          <TextField
            label="주차장 ID"
            value={lotId}
            onChange={(e) => setLotId(e.target.value.replace(/[^0-9]/g, ''))}
            size="small"
            disabled={loading}
            sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0 }}
            helperText="맵의 parkingLotId"
          />
          <Button
            variant="contained"
            onClick={run}
            disabled={!canRun}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
            sx={{ flexShrink: 0, mt: { sm: 0.25 } }}
          >
            {loading ? '생성 중…' : '생성'}
          </Button>
        </Stack>
        {loading && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            서버에서 층마다 지도를 렌더하고 CCTV 전수로 계수를 검증합니다. 층 수에 따라 수십 초 걸립니다.
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>이미지와 계수는 한 세트입니다</AlertTitle>
            아래 542×382 이미지를 HT 에 등록했다면, ht_location 의 <code>HT_MAP_TRANSFORM</code> 도
            반드시 같은 생성 결과로 교체하세요. 한쪽만 바꾸면 월패드에서 주차 위치가 밀립니다.
            HT 지도번호는 각 층 이름과 동일하게 등록합니다.
          </Alert>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                HT_MAP_TRANSFORM
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<CopyIcon fontSize="small" />} onClick={copyTransform}>
                {copied ? '복사됨' : '복사'}
              </Button>
            </Stack>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: '0.75rem',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {result.transform_env}
            </Box>
          </Paper>

          <Stack spacing={2}>
            {result.floors.map((f) => (
              <Paper key={f.name} variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    {f.name}
                  </Typography>
                  <VerdictChip floor={f} />
                </Stack>

                {f.validated && (f.max_error_px > 1 || f.out_of_range > 0) && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    검증 오차가 허용범위(1px)를 벗어났습니다. 이 층은 등록하지 말고 맵 데이터를 확인하세요.
                  </Alert>
                )}
                {!f.validated && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    생성기 출력에서 검증 수치를 읽지 못했습니다. 이미지는 만들어졌지만 계수가 맞는지
                    확인되지 않았으니, 아래 로그를 확인하세요.
                  </Alert>
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box
                    component="img"
                    src={f.ht_png}
                    alt={`${f.name} 월패드 지도 미리보기`}
                    sx={{
                      width: 542,
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      계수 (542×382 기준)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        mt: 0.5,
                        mb: 2,
                        p: 1.5,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        overflowX: 'auto',
                      }}
                    >
                      {JSON.stringify(f.transform, null, 2)}
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon fontSize="small" />}
                        onClick={() => downloadDataUrl(f.ht_png, `${filePrefix}${f.name}_542x382.png`)}
                      >
                        HT 등록용 542×382
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon fontSize="small" />}
                        onClick={() => downloadDataUrl(f.orig_png, `${filePrefix}${f.name}_2168x1528.png`)}
                      >
                        원본 2168×1528
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" component="div">
            생성 로그
          </Typography>
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontSize: '0.72rem',
              maxHeight: 200,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {result.log || '(출력 없음)'}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            생성 절차와 주의사항은{' '}
            <Link
              href="https://github.com/LUXROBO/Luzer/blob/develop/services/ht_location/README.md"
              target="_blank"
              rel="noreferrer"
            >
              ht_location README
            </Link>{' '}
            를 참고하세요.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default WallpadMapSection;
